/* eslint-disable react/display-name */
import React, {useCallback, useEffect, useState} from 'react'
import {Button, Empty, Icon, Input, Spin, Table} from 'antd'
import Highlighter from 'react-highlight-words'
import {SlaCondition, SlaPolicy, SlaPolicyFunc} from './model'
import {nullOrEmptyObject} from '../../common-misc'
import {PriorityLevel} from '../../incident-management/priority'
import {User} from '../../authorization-module/user-role/duck/model'
import {SupportTeamManagement} from '../../authorization-module/support-team-management/model'
import { convertCamelCaseToSentenceCase } from '../../knowledge-management/util'
import { Categories, Item, SubCategory } from '../../incident-management/category'
import { GeneralIncidentSetting } from '../../incident-management/general-incident-setting'
import { People } from '../../authorization-module/people/model'

interface Props {
    slaPolicy: SlaPolicy
    isView: boolean
    handleToUpdate: (boolean) => void
    priority: PriorityLevel[]
    user: User[]
    supportTeam: SupportTeamManagement[]
    categories: Categories[]
    subCategories: SubCategory[]
    items: Item[]
    impacts: PriorityLevel[]
    urgencies: PriorityLevel[]
    generalIncidentSetting: GeneralIncidentSetting
    dataRequesterEmail: People[]
}

export const SlaPolicyFormConditionList: React.FC<Props> = (props: Props) => {
    const [searchText, setSearchText] = useState<string>('')
    const [, updateState] = useState()
    const forceUpdate = useCallback(() => updateState(Object), [])
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState<SlaCondition[]>([])

    useEffect(() => {
        console.log('props.slaPolicy.conditions', props.slaPolicy.conditions);
        
        if (props.slaPolicy.conditions) {
            setData(props.slaPolicy.conditions)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.slaPolicy.conditions])

    const deleteSplitCondition = (splitCondition: SlaCondition) => {
        setIsLoading(true)
        setTimeout(() => {
            if (SlaPolicyFunc.deleteConditionIfExist(props.slaPolicy, splitCondition.rule, splitCondition.value)) {
                forceUpdate()
                setData(props.slaPolicy.conditions)
                if (nullOrEmptyObject(props.slaPolicy.conditions)) {
                    props.handleToUpdate(true)
                }
                setIsLoading(false)
            } else {
                setIsLoading(false)
            }
        }, 500)
    }

    const getColumnSearchProps = (dataIndex: any) => ({
        // eslint-disable-next-line react/display-name
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters }:any):any => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e:any) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm)}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Button
                    type="primary"
                    onClick={() => handleSearch(selectedKeys, confirm)}
                    icon="search"
                    size="small"
                    style={{ width: 90, marginRight: 8, color: 'red'}}
                >
                    Search
                </Button>
                <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                    Reset
                </Button>
            </div>
        ),
        // eslint-disable-next-line react/display-name
        filterIcon: (filtered:any) => (
            <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        onFilter: (value:any, record:any) => {
            if (dataIndex === 'value') {
                return convertConditionValue(record).toLowerCase().includes(value.toLowerCase())
            } else if (dataIndex === 'rule') {
                return convertConditionRule(record.rule).toLowerCase().includes(value.toLowerCase())
            } else {
                return record[dataIndex]
                    .toString()
                    .toLowerCase()
                    .includes(value.toLowerCase())
            }
        },
        onFilterDropdownVisibleChange: (visible:any) => {
            if (visible) {
                console.log(visible)
            }
        },
        // eslint-disable-next-line react/display-name
        render: (text:any) => (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]}
                autoEscape
                textToHighlight={text}
            />
        )
    })

    const handleSearch = (selectedKeys: any, confirm: any) => {
        confirm()
        setSearchText(selectedKeys[0])
    }

    const handleReset = (clearFilters: any) => {
        clearFilters()
        setSearchText('')
    }

    const convertConditionRule = (rule) => {
        if (rule === 'supportTeamId') {
            return 'Group'
        } else if (rule === 'userId') {
            return 'Assignee'
        } else if (rule === 'priorityId') {
            return 'Priority'
        } else if (rule === 'categoryId') {
            return 'Category'
        } else if (rule === 'subCategoryId') {
            return 'Sub Category'
        } else if (rule === 'itemId') {
            return 'Item'
        } else if (rule === 'impactId') {
            return 'Impact'
        } else if (rule === 'urgencyId') {
            return 'Urgency'
        } else if (rule === 'channelId') {
            return 'Channel'
        } else if (rule === 'requesterEmail') {
            return 'RequesterEmail'
        } else {
            const convert = rule.replaceAll('_', ' ')
            return convertCamelCaseToSentenceCase(convert) 
        }
    }

    const convertConditionValue = (condition: SlaCondition):string => {
        if (condition.rule === 'supportTeamId') {
            if (props.supportTeam) {
                if (typeof condition.value === 'number' || typeof condition.value === 'string') {
                    if (typeof condition.value === 'string') {
                        const spiltText = condition.value.split(',')
                        if (spiltText.length > 0) {
                            const supportTeamNameList = props.supportTeam.filter(it => spiltText.includes(it.id.toString())).map(it => it.name);
                            return supportTeamNameList?.join(', ');
                        }
                    } else {
                        const supportTeam = props.supportTeam.find(it => it.id?.toString() === condition.value.toString());
                        return supportTeam?.name || '';
                    }
                } else {
                    const supportTeamNameList = props.supportTeam.filter(it => condition.value.includes(it.id)).map(it => it.name);
                    return supportTeamNameList?.join(', ');
                }
            }
        } else if (condition.rule === 'userId') {
            if (props.user) {
                if (typeof condition.value === 'number' || typeof condition.value === 'string') {
                    if (typeof condition.value === 'string') {
                        const spiltText = condition.value.split(',')
                        if (spiltText.length > 0) {
                            const userNameList = props.user.filter(it => spiltText.includes(it.id.toString())).map(it => it.fullName);
                            return userNameList?.join(', ');
                        }
                    } else {
                        const supportTeam = props.user.find(it => it.id?.toString() === condition.value.toString());
                        return supportTeam?.fullName || '';
                    }
                } else {
                    const userNameList = props.user.filter(it => condition.value.includes(it.id)).map(it => it.fullName);
                    return userNameList?.join(', ');
                }
            }
        } else if (condition.rule === 'priorityId') {
            if (props.priority) {
                if (typeof condition.value === 'number' || typeof condition.value === 'string') {
                    if (typeof condition.value === 'string') {
                        const spiltText = condition.value.split(',')
                        if (spiltText.length > 0) {
                            const priorityNameList = props.priority.filter(it => spiltText.includes(it.id.toString())).map(it => it.name);
                            return priorityNameList?.join(', ');
                        }
                    } else {
                        const priority = props.priority.find(it => it.id?.toString() === condition.value.toString())
                        return priority?.name!!
                    }
                } else {
                    const priorityNameList = props.priority.filter(it => condition.value.includes(it.id)).map(it => it.name);
                    return priorityNameList?.join(', ');
                }
            }
        } else if (condition.rule === 'categoryId') {
            if (props.categories) {
                if (typeof condition.value === 'number' || typeof condition.value === 'string') {
                    if (typeof condition.value === 'string') {
                        const spiltText = condition.value.split(',')
                        if (spiltText.length > 0) {
                            const categoriesNameList = props.categories.filter(it => spiltText.includes(it.id.toString())).map(it => it.name);
                            return categoriesNameList?.join(', ');
                        }
                    } else {
                        const categories = props.categories.find(it => it.id?.toString() === condition.value.toString())
                        return categories?.name!!
                    }
                } else {
                    const categoriesNameList = props.categories.filter(it => condition.value.includes(it.id)).map(it => it.name);
                    return categoriesNameList?.join(', ');
                }
            }
        } else if (condition.rule === 'subCategoryId') {
            if (props.subCategories) {
                if (typeof condition.value === 'number' || typeof condition.value === 'string') {
                    if (typeof condition.value === 'string') {
                        const spiltText = condition.value.split(',')
                        if (spiltText.length > 0) {
                            const subCategoriesNameList = props.subCategories.filter(it => spiltText.includes(it.id.toString())).map(it => it.subCategory);
                            return subCategoriesNameList?.join(', ');
                        }
                    } else {
                        const subCategories = props.subCategories.find(it => it.id?.toString() === condition.value.toString())
                        return subCategories?.subCategory!!
                    }
                } else {
                    const subCategoriesNameList = props.subCategories.filter(it => condition.value.includes(it.id)).map(it => it.subCategory);
                    return subCategoriesNameList?.join(', ');
                }
            }
        } else if (condition.rule === 'itemId') {
            if (props.items) {
                if (typeof condition.value === 'number' || typeof condition.value === 'string') {
                    if (typeof condition.value === 'string') {
                        const spiltText = condition.value.split(',')
                        if (spiltText.length > 0) {
                            const items = props.items.filter(it => spiltText.includes(it.id.toString())).map(it => it.item);
                            return items?.join(', ');
                        }
                    } else {
                        const items = props.items.find(it => it.id?.toString() === condition.value.toString())
                        return items?.item!!
                    }
                } else {
                    const itemsNameList = props.items.filter(it => condition.value.includes(it.id)).map(it => it.item);
                    return itemsNameList?.join(', ');
                }
            }
        } else if (condition.rule === 'impactId') {
            if (props.impacts) {
                if (typeof condition.value === 'number' || typeof condition.value === 'string') {
                    if (typeof condition.value === 'string') {
                        const spiltText = condition.value.split(',')
                        if (spiltText.length > 0) {
                            const impactsNameList = props.impacts.filter(it => spiltText.includes(it.id.toString())).map(it => it.name);
                            return impactsNameList?.join(', ');
                        }
                    } else {
                        const impacts = props.impacts.find(it => it.id?.toString() === condition.value.toString())
                        return impacts?.name!!
                    }
                } else {
                    const impactsNameList = props.impacts.filter(it => condition.value.includes(it.id)).map(it => it.name);
                    return impactsNameList?.join(', ');
                }
            }
        } else if (condition.rule === 'urgencyId') {
            if (props.urgencies) {
                if (typeof condition.value === 'number' || typeof condition.value === 'string') {
                    if (typeof condition.value === 'string') {
                        const spiltText = condition.value.split(',')
                        if (spiltText.length > 0) {
                            const urgenciesNameList = props.urgencies.filter(it => spiltText.includes(it.id.toString())).map(it => it.name);
                            return urgenciesNameList?.join(', ');
                        }
                    } else {
                        const urgencies = props.urgencies.find(it => it.id?.toString() === condition.value.toString())
                        return urgencies?.name!!
                    }
                } else {
                    const urgenciesNameList = props.urgencies.filter(it => condition.value.includes(it.id)).map(it => it.name);
                    return urgenciesNameList?.join(', ');
                }
            }
        } else if (condition.rule === 'channelId') {
            if (props.generalIncidentSetting.channels) {
                if (typeof condition.value === 'number' || typeof condition.value === 'string') {
                    if (typeof condition.value === 'string') {
                        const spiltText = condition.value.split(',')
                        if (spiltText.length > 0) {
                            const channelsNameList = props.generalIncidentSetting.channels.filter(it => spiltText.includes(it.id.toString())).map(it => it.value);
                            return channelsNameList?.join(', ');
                        }
                    } else {
                        const channels = props.generalIncidentSetting.channels.find(it => it.id?.toString() === condition.value.toString())
                        return channels?.value!!
                    }
                } else {
                    const channelsNameList = props.generalIncidentSetting.channels.filter(it => condition.value.includes(it.id)).map(it => it.value);
                    return channelsNameList?.join(', ');
                }
            }
        } else if (condition.rule === 'requesterEmail') {
            if (props.dataRequesterEmail) {
                if (typeof condition.value === 'number' || typeof condition.value === 'string') {
                    if (typeof condition.value === 'string') {
                        const spiltText = condition.value.split(',')
                        if (spiltText.length > 0) {
                            const requesterNameList = props.dataRequesterEmail.filter(it => spiltText.includes(it.id.toString())).map(it => it.fullName);
                            return requesterNameList?.join(', ');
                        }
                    } else {
                        const requester = props.dataRequesterEmail.find(it => it.id?.toString() === condition.value.toString())
                        return requester?.fullName!!
                    }
                } else {
                    const requesterNameList = props.dataRequesterEmail.filter(it => condition.value?.includes(it.id?.toString())).map(it => it.fullName);
                    return requesterNameList?.join(', ');
                }
            }
        } else {
            return condition.value.toString()
        }
    }

    const columns = [
        {
            title: '',
            width: '10%',
            // eslint-disable-next-line react/display-name
            render: (splitCondition: SlaCondition) =>
                // eslint-disable-next-line react/prop-types
                <span ><Icon type="delete" onClick={() => deleteSplitCondition(splitCondition)} className= { props.isView ? 'disabled-icon' : 'deleteButton'} /></span>
        },
        {
            title: 'Rule',
            dataIndex: 'rule',
            key: 'id',
            width: '30%',
            sorter: (a, b) => a.rule?.localeCompare(b.rule),
            ...getColumnSearchProps('rule'),
            render: (row: string) => (
                <>
                    <div>
                        <span>{convertConditionRule(row)}</span>
                    </div>
                </>
            )
        },
        {
                title: 'Condition',
                dataIndex: 'operator',
                key: 'operator',
                width: '10%',
                sorter: (a, b) => a.condition?.localeCompare(b.condition),
                ...getColumnSearchProps('operator'),
                render: (row: string) => (
                    <>
                        <div>
                            <span>{row}</span>
                        </div>
                    </>
                )
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
            width: '50%',
            sorter: (a, b) => convertConditionValue(a).localeCompare(convertConditionValue(b)),
            ...getColumnSearchProps('value'),
            render: (row: string, data: SlaCondition) => (
                <>
                    <div>
                        <span>{convertConditionValue(data)}</span>
                    </div>
                </>
            )
        }
    ]

    const renderClassByIsViews = () => {
        if (props.isView) {
            return ''
        } else {
            return 'table table-striped'
        }
    }
    // console.log(data)
    return (
        <>
            <div>
                {nullOrEmptyObject(props.slaPolicy.conditions)
                    ? (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}/>
                    ) : (
                        isLoading ? <Spin style={{textAlign: 'center'}}/> : <Table
                            columns={columns}
                            dataSource={data}
                            pagination={{ pageSize: 5 }} size="small" className={renderClassByIsViews()}
                        />
                    )
                }
            </div>
        </>
    )
}
